import { FormFieldProps } from '../../FormField/FormField';
import * as z from 'zod';
import {
  RefineParams,
  superRefineHelper,
  superRefinePhoneInputHelper,
} from './helpers/superRefineHelpers';
import {
  getDateWithOffset,
  isNullOrUndefined,
  safeParseRegex,
} from '@r-and-a-shared-ui/utils';
import { hasValue } from '@r-and-a-shared-ui/utils';
import { FormDateFieldProps } from '../../FormDateField/FormDateField';
export const defaultValidationMessage = 'Please input a value.';

export const defaultValidationRegex = /^((.|\n)*|^(?=\s*$))$/; // matches anything
export const defaultNumberRegex = new RegExp('^[0-9]*$'); // matches any number

export const validateInputField = (
  fieldData: FormFieldProps,
  fieldToCompare?: FormFieldProps,
  dependencyCriteria?: string | undefined,
  defaultPattern?: RegExp,
):
  | z.ZodEffects<
      z.ZodOptional<z.ZodNullable<z.ZodString>>,
      string | null | undefined,
      string | null | undefined
    >
  | z.ZodEffects<z.ZodString, string, string> => {
  const pattern = fieldData.validationRegex
    ? safeParseRegex(fieldData.validationRegex) ||
      defaultPattern ||
      defaultValidationRegex
    : defaultValidationRegex;

  const refineParams: RefineParams = {
    fieldData,
    fieldToCompare,
    dependencyCriteria,
    pattern,
  };

  return z
    .string()
    .nullish()
    .superRefine((value, ctx) => {
      const htmlElement = document.getElementById(fieldData.name);

      if (fieldData.isHidden || !htmlElement) {
        return {};
      }

      const refinedValue = value ?? '';

      return superRefineHelper(
        {
          value: refinedValue,
          ctx,
        },
        refineParams,
      );
    });
};

export const validateTextField = (
  fieldData: FormFieldProps,
  fieldToCompare?: FormFieldProps,
  dependencyCriteria?: string | undefined,
):
  | z.ZodEffects<
      z.ZodOptional<z.ZodNullable<z.ZodString>>,
      string | null | undefined,
      string | null | undefined
    >
  | z.ZodEffects<z.ZodString, string, string> =>
  validateInputField(
    fieldData,
    fieldToCompare,
    dependencyCriteria,
    defaultValidationRegex,
  );

export const validateNumberField = (
  fieldData: FormFieldProps,
  fieldToCompare?: FormFieldProps,
  dependencyCriteria?: string | undefined,
):
  | z.ZodEffects<
      z.ZodOptional<z.ZodNullable<z.ZodString>>,
      string | null | undefined,
      string | null | undefined
    >
  | z.ZodEffects<z.ZodString, string, string> =>
  validateInputField(
    fieldData,
    fieldToCompare,
    dependencyCriteria,
    defaultNumberRegex,
  );

export const validateMultipleChoiceField = (
  fieldData: FormFieldProps,
): z.ZodEffects<z.ZodAny, any, any> =>
  z.any().superRefine((val, ctx) => {
    const htmlElement = document.getElementById(fieldData.name);

    if (!fieldData.isRequired || !htmlElement) {
      return z.string().optional();
    }

    if (!val || (Array.isArray(val) && !val.length)) {
      ctx.addIssue({
        path: [fieldData.name],
        code: z.ZodIssueCode.custom,
        fatal: true,
      });
      return;
    }

    if (fieldData.entireObjectSelection) {
      return;
    }
    return z.string().min(fieldData.isRequired ? 1 : 0);
  });

export const validateDateField = (
  fieldData: FormDateFieldProps,
): z.ZodEffects<z.ZodAny, any, any> =>
  z.any().superRefine((val, ctx) => {
    const dateWithOffset = getDateWithOffset(fieldData.yearOffset);
    if (val instanceof Date && val.getTime() <= dateWithOffset) {
      return;
    }
    if (val === undefined && !fieldData.isRequired) {
      return;
    }

    ctx.addIssue({
      path: [fieldData.name],
      code: z.ZodIssueCode.custom,
      fatal: true,
      message: fieldData.validationMessage,
    });
    return;
  });

export const validatePhoneField = (
  fieldData: FormFieldProps,
  fieldToCompare?: FormFieldProps,
  dependencyCriteria?: string | undefined,
  defaultPattern?: RegExp,
): z.ZodEffects<
  z.ZodString | z.ZodOptional<z.ZodNullable<z.ZodString>>,
  string | null | undefined,
  string | null | undefined
> => {
  const pattern = fieldData.validationRegex
    ? safeParseRegex(fieldData.validationRegex) ||
      defaultPattern ||
      defaultValidationRegex
    : defaultValidationRegex;

  const refineParams: RefineParams = {
    fieldData,
    fieldToCompare,
    dependencyCriteria,
    pattern,
  };

  const validator = fieldData.isRequired
    ? z.string().min(1)
    : z.string().nullish();

  return validator.superRefine((value, ctx) => {
    const refinedValue = isNullOrUndefined(value) ? '' : value;

    return superRefinePhoneInputHelper(
      {
        value: refinedValue,
        ctx,
      },
      refineParams,
    );
  });
};

export const validateAttachmentField = (
  fieldData: FormFieldProps,
): z.ZodEffects<z.ZodAny, any, any> =>
  z.any().superRefine((val, ctx) => {
    const htmlElement = document.getElementById(fieldData.name);
    if (!fieldData.isRequired || !htmlElement) {
      return;
    }

    const hasAttachmentValue =
      val !== undefined &&
      val !== null &&
      typeof val === 'object' &&
      hasValue((val as { name?: string }).name) &&
      hasValue((val as { content?: string }).content);

    if (hasAttachmentValue) {
      return;
    }

    ctx.addIssue({
      path: [fieldData.name],
      code: z.ZodIssueCode.custom,
      fatal: true,
    });
  });
